const DEFAULT_LANDING_PAGE_URL = "https://sendbot.co";
const DEFAULT_SENDBOT_API_URL = "https://api.sendbot.co";

const runtimeValue = (key: string): string | undefined => {
  if (typeof process === "undefined" || !process.env) return undefined;
  return process.env[key];
};
const toBoolean = (value: string | undefined): boolean | undefined =>
  value === undefined ? undefined : value === "true";
const toNodeEnv = (value: string | undefined) =>
  value === "development" ||
  value === "staging" ||
  value === "production" ||
  value === "test"
    ? value
    : undefined;
const toOptionalString = (value: string | undefined) =>
  value?.trim() || undefined;
const isValidAbsoluteUrl = (value: string) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};
const splitOrigins = (value: string) =>
  value
    .split(/[\s,]+/)
    .map((origin) => origin.trim())
    .filter(Boolean);

// Public values stay explicit so Vite can replace only this allowlist at build time.
const publicEnv = {
  NEXT_PUBLIC_COOLIFY_URL: process.env.NEXT_PUBLIC_COOLIFY_URL,
  NEXT_PUBLIC_LANDING_PAGE_URL: process.env.NEXT_PUBLIC_LANDING_PAGE_URL,
  NEXT_PUBLIC_SENDBOT_API_URL: process.env.NEXT_PUBLIC_SENDBOT_API_URL,
  NEXT_PUBLIC_ALLOWED_ORIGINS: process.env.NEXT_PUBLIC_ALLOWED_ORIGINS,
  NEXT_PUBLIC_ALLOWED_PARENT_ORIGINS:
    process.env.NEXT_PUBLIC_ALLOWED_PARENT_ORIGINS,
  NEXT_PUBLIC_IMAGE_CDN: process.env.NEXT_PUBLIC_IMAGE_CDN,
  NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION:
    process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION,
  NEXT_PUBLIC_YANDEX_VERIFICATION: process.env.NEXT_PUBLIC_YANDEX_VERIFICATION,
  NEXT_PUBLIC_GOOGLE_VERIFICATION: process.env.NEXT_PUBLIC_GOOGLE_VERIFICATION,
  NEXT_PUBLIC_BING_VERIFICATION: process.env.NEXT_PUBLIC_BING_VERIFICATION,
  NEXT_PUBLIC_FACEBOOK_VERIFICATION:
    process.env.NEXT_PUBLIC_FACEBOOK_VERIFICATION,
  NEXT_PUBLIC_CONTENTFUL_SPACE_ID: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID,
  NEXT_PUBLIC_VERCEL_ANALYTICS_ID: process.env.NEXT_PUBLIC_VERCEL_ANALYTICS_ID,
  NEXT_PUBLIC_ENABLE_VERCEL_INSIGHTS:
    process.env.NEXT_PUBLIC_ENABLE_VERCEL_INSIGHTS,
  NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
  NEXT_PUBLIC_GA_ID: process.env.NEXT_PUBLIC_GA_ID,
  NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
  NEXT_PUBLIC_ENABLE_AUTH: process.env.NEXT_PUBLIC_ENABLE_AUTH,
};

export const env = {
  NODE_ENV: toNodeEnv(runtimeValue("NODE_ENV")),
  VERCEL_URL: toOptionalString(runtimeValue("VERCEL_URL")),
  COOLIFY_URL: toOptionalString(runtimeValue("COOLIFY_URL")),
  ALLOWED_ORIGINS: toOptionalString(runtimeValue("ALLOWED_ORIGINS")),
  BETTERSTACK_API_URL: toOptionalString(runtimeValue("BETTERSTACK_API_URL")),
  BETTERSTACK_API_TOKEN: toOptionalString(
    runtimeValue("BETTERSTACK_API_TOKEN"),
  ),
  BETTERSTACK_MONITOR_IDS: toOptionalString(
    runtimeValue("BETTERSTACK_MONITOR_IDS"),
  ),
  CONTENTFUL_SPACE_ID: toOptionalString(runtimeValue("CONTENTFUL_SPACE_ID")),
  CONTENTFUL_ACCESS_TOKEN: toOptionalString(
    runtimeValue("CONTENTFUL_ACCESS_TOKEN"),
  ),
  RESEND_API_KEY: toOptionalString(runtimeValue("RESEND_API_KEY")),
  INTERNAL_HEALTH_TOKEN: toOptionalString(
    runtimeValue("INTERNAL_HEALTH_TOKEN"),
  ),
  NEXT_PUBLIC_COOLIFY_URL: toOptionalString(publicEnv.NEXT_PUBLIC_COOLIFY_URL),
  NEXT_PUBLIC_LANDING_PAGE_URL:
    toOptionalString(publicEnv.NEXT_PUBLIC_LANDING_PAGE_URL) ??
    toOptionalString(runtimeValue("LANDING_PAGE_URL")) ??
    DEFAULT_LANDING_PAGE_URL,
  NEXT_PUBLIC_SENDBOT_API_URL:
    toOptionalString(publicEnv.NEXT_PUBLIC_SENDBOT_API_URL) ??
    DEFAULT_SENDBOT_API_URL,
  NEXT_PUBLIC_ALLOWED_ORIGINS: toOptionalString(
    publicEnv.NEXT_PUBLIC_ALLOWED_ORIGINS,
  ),
  NEXT_PUBLIC_ALLOWED_PARENT_ORIGINS: toOptionalString(
    publicEnv.NEXT_PUBLIC_ALLOWED_PARENT_ORIGINS,
  ),
  NEXT_PUBLIC_IMAGE_CDN: toOptionalString(publicEnv.NEXT_PUBLIC_IMAGE_CDN),
  NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION: toOptionalString(
    publicEnv.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION,
  ),
  NEXT_PUBLIC_YANDEX_VERIFICATION: toOptionalString(
    publicEnv.NEXT_PUBLIC_YANDEX_VERIFICATION,
  ),
  NEXT_PUBLIC_GOOGLE_VERIFICATION: toOptionalString(
    publicEnv.NEXT_PUBLIC_GOOGLE_VERIFICATION,
  ),
  NEXT_PUBLIC_BING_VERIFICATION: toOptionalString(
    publicEnv.NEXT_PUBLIC_BING_VERIFICATION,
  ),
  NEXT_PUBLIC_FACEBOOK_VERIFICATION: toOptionalString(
    publicEnv.NEXT_PUBLIC_FACEBOOK_VERIFICATION,
  ),
  NEXT_PUBLIC_CONTENTFUL_SPACE_ID: toOptionalString(
    publicEnv.NEXT_PUBLIC_CONTENTFUL_SPACE_ID,
  ),
  NEXT_PUBLIC_VERCEL_ANALYTICS_ID: toOptionalString(
    publicEnv.NEXT_PUBLIC_VERCEL_ANALYTICS_ID,
  ),
  NEXT_PUBLIC_ENABLE_VERCEL_INSIGHTS: toBoolean(
    publicEnv.NEXT_PUBLIC_ENABLE_VERCEL_INSIGHTS,
  ),
  NEXT_PUBLIC_GTM_ID: toOptionalString(publicEnv.NEXT_PUBLIC_GTM_ID),
  NEXT_PUBLIC_GA_ID: toOptionalString(publicEnv.NEXT_PUBLIC_GA_ID),
  NEXT_PUBLIC_SENTRY_DSN: toOptionalString(publicEnv.NEXT_PUBLIC_SENTRY_DSN),
  NEXT_PUBLIC_ENABLE_AUTH: toBoolean(publicEnv.NEXT_PUBLIC_ENABLE_AUTH),
} as const;

let hasValidatedEnv = false;
export function validateEnv() {
  if (hasValidatedEnv) return;
  const structuralErrors: string[] = [];
  const warnings: string[] = [];
  if (typeof process !== "undefined" && !env.NODE_ENV)
    structuralErrors.push(
      "NODE_ENV inválido. Use development, staging, production ou test.",
    );
  for (const [label, value] of [
    ["NEXT_PUBLIC_SENDBOT_API_URL", env.NEXT_PUBLIC_SENDBOT_API_URL],
    ["NEXT_PUBLIC_LANDING_PAGE_URL", env.NEXT_PUBLIC_LANDING_PAGE_URL],
  ] as const) {
    if (!isValidAbsoluteUrl(value))
      structuralErrors.push(`${label} deve ser uma URL absoluta válida.`);
  }
  for (const [label, value] of [
    ["BETTERSTACK_API_URL", env.BETTERSTACK_API_URL],
    ["NEXT_PUBLIC_IMAGE_CDN", env.NEXT_PUBLIC_IMAGE_CDN],
  ] as const) {
    if (value && !isValidAbsoluteUrl(value))
      structuralErrors.push(`${label} deve ser uma URL absoluta válida.`);
  }
  const origins = [env.ALLOWED_ORIGINS, env.NEXT_PUBLIC_ALLOWED_ORIGINS]
    .flatMap((value) => (value ? splitOrigins(value) : []))
    .filter((value) => value !== "*");
  for (const origin of origins)
    if (!isValidAbsoluteUrl(origin))
      structuralErrors.push(`Origem CORS inválida: ${origin}`);
  if (
    env.BETTERSTACK_MONITOR_IDS &&
    (!env.BETTERSTACK_API_URL || !env.BETTERSTACK_API_TOKEN)
  )
    structuralErrors.push(
      "BETTERSTACK_MONITOR_IDS exige BETTERSTACK_API_URL e BETTERSTACK_API_TOKEN.",
    );
  if (
    env.NEXT_PUBLIC_ENABLE_VERCEL_INSIGHTS &&
    !env.NEXT_PUBLIC_VERCEL_ANALYTICS_ID
  )
    warnings.push("Vercel Insights ativo sem analytics ID.");
  if (!env.NEXT_PUBLIC_GTM_ID && !env.NEXT_PUBLIC_GA_ID)
    warnings.push("Google Analytics desativado.");
  if (!env.NEXT_PUBLIC_SENTRY_DSN) warnings.push("Sentry desativado.");
  if (structuralErrors.length)
    throw new Error(
      [
        "Falha na validação de variáveis de ambiente:",
        ...structuralErrors.map((value) => `- ${value}`),
      ].join("\n"),
    );
  if (warnings.length && typeof process !== "undefined")
    console.warn(
      ["Avisos da landing:", ...warnings.map((value) => `- ${value}`)].join(
        "\n",
      ),
    );
  hasValidatedEnv = true;
}
